import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Aviso, Campo, Modal } from "../componentes/piezas";
import { SelectorIdioma, useIdioma, type Idioma } from "../lib/idioma";
import { useSesionPortal } from "./sesionPortal";
import PortalEntrada from "./PortalEntrada";
import PortalInicio from "./PortalInicio";
import PortalHistorial from "./PortalHistorial";
import PortalValoraciones from "./PortalValoraciones";
import PortalCatalogo from "./PortalCatalogo";
import PortalReservas from "./PortalReservas";
import PortalNoticias from "./PortalNoticias";
import PortalAlta from "./PortalAlta";
import PortalContrato from "./PortalContrato";

type Pestana =
  | "inicio"
  | "catalogo"
  | "reservas"
  | "noticias"
  | "valoraciones"
  | "historial"
  | "contrato";

/**
 * Portal del socio. Es una aplicación aparte de la de gestión: sesión propia,
 * datos propios y pensada para el móvil. Habla el idioma del club, o el que
 * el socio elija (ver lib/idioma.tsx).
 */
export default function PortalApp() {
  const { datos, cargando, salir } = useSesionPortal();
  const { t, aplicarPorDefecto, aplicarDelSocio } = useIdioma();
  const [pestana, setPestana] = useState<Pestana>("inicio");
  const [cambiandoPassword, setCambiandoPassword] = useState(false);

  /*
   * El suyo si lo tiene, y si no el del club, que es lo que ve la primera vez.
   *
   * El del socio pisa lo que quedara elegido en este telefono, porque es la
   * ultima cosa que decidio alguien a proposito: el mismo desde otro aparato, o
   * el equipo desde su ficha. El del club es solo el valor por defecto y no
   * pisa nada, que si no, un socio que eligio neerlandes al pedir el alta
   * volveria al castellano del club en cuanto entrara en su portal.
   */
  const suyo = datos?.socio.idioma;
  const delClub = datos?.club.idioma;
  useEffect(() => aplicarDelSocio(suyo), [suyo, aplicarDelSocio]);
  useEffect(() => aplicarPorDefecto(delClub), [delClub, aplicarPorDefecto]);

  /*
   * Y lo que elija se guarda en el servidor, no solo en el telefono: si viviera
   * solo aqui, el socio veria el portal en neerlandes y los correos de la cuota
   * le seguirian llegando en castellano, que es justo lo que se venia a
   * arreglar.
   *
   * Sale del gesto de tocar el selector, no de un efecto sobre el idioma de
   * ahora. Mirando el idioma de ahora no se distingue lo que acaba de elegir
   * este socio de lo que quedo guardado en el aparato: en la tablet del
   * mostrador, donde los socios entran uno detras de otro, eso le escribia a
   * cada uno en la ficha el idioma del anterior, y deshacia el que el equipo
   * acabara de ponerle.
   */
  function guardarIdioma(nuevo: Idioma) {
    void api("/portal/idioma", { metodo: "POST", cuerpo: { idioma: nuevo } }).catch(() => {
      // Si falla, el socio sigue viendo su idioma en este telefono; lo unico
      // que no viaja es el correo. Si lo vuelve a tocar, se vuelve a intentar.
    });
  }

  // La solicitud de alta es pública: la rellena quien aún no es socio.
  const partes = window.location.pathname
    .replace(/^\/portal\/?/, "")
    .split("/");
  if (partes[1] === "alta") return <PortalAlta club={partes[0]!} />;

  if (cargando) return <div className="cargando">{t("general.cargando")}</div>;
  if (!datos) return <PortalEntrada />;

  const pestanas: [Pestana, string][] = [
    ["inicio", t("portal.pestana.inicio")],
    ["catalogo", t("portal.pestana.catalogo")],
    ["reservas", t("portal.pestana.reservas")],
    ["noticias", t("portal.pestana.noticias")],
    ["valoraciones", t("portal.pestana.valoraciones")],
    ["historial", t("portal.pestana.historial")],
    ["contrato", t("portal.pestana.contrato")],
  ];

  return (
    <div className="portal">
      <header className="portal-cabecera">
        <div>
          <strong>{datos.club.nombre}</strong>
          <div className="suave pequeno">
            {t("portal.cabecera.socioNumero", {
              nombre: datos.socio.nombre,
              numero: datos.socio.numeroSocio,
            })}
          </div>
        </div>
        <div className="fila">
          <SelectorIdioma compacto alElegir={guardarIdioma} />
          <button
            className="boton fantasma"
            onClick={() => setCambiandoPassword(true)}
          >
            {t("portal.cabecera.contrasena")}
          </button>
          <button className="boton secundario" onClick={() => void salir()}>
            {t("general.salir")}
          </button>
        </div>
      </header>

      <nav className="portal-pestanas">
        {pestanas.map(([clave, texto]) => (
          <button
            key={clave}
            className={`boton ${pestana === clave ? "" : "secundario"}`}
            onClick={() => setPestana(clave)}
          >
            {texto}
          </button>
        ))}
      </nav>

      <main className="portal-contenido">
        {pestana === "inicio" && <PortalInicio />}
        {pestana === "catalogo" && (
          <PortalCatalogo alReservar={() => setPestana("reservas")} />
        )}
        {pestana === "reservas" && <PortalReservas />}
        {pestana === "noticias" && <PortalNoticias />}
        {pestana === "valoraciones" && <PortalValoraciones />}
        {pestana === "historial" && <PortalHistorial />}
        {pestana === "contrato" && <PortalContrato />}
      </main>

      {cambiandoPassword && (
        <DialogoPasswordSocio alCerrar={() => setCambiandoPassword(false)} />
      )}
    </div>
  );
}

function DialogoPasswordSocio({ alCerrar }: { alCerrar: () => void }) {
  const { t } = useIdioma();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetir, setRepetir] = useState("");
  const [error, setError] = useState("");
  const [hecho, setHecho] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const valida = actual.length > 0 && nueva.length >= 8 && nueva === repetir;

  return (
    <Modal
      titulo={t("contrasena.titulo")}
      alCerrar={alCerrar}
      acciones={
        hecho ? (
          <button className="boton" onClick={alCerrar}>
            {t("general.cerrar")}
          </button>
        ) : (
          <>
            <button className="boton secundario" onClick={alCerrar}>
              {t("general.cancelar")}
            </button>
            <button
              className="boton"
              disabled={enviando || !valida}
              onClick={async () => {
                setEnviando(true);
                setError("");
                try {
                  await api("/portal/cambiar-password", {
                    metodo: "POST",
                    cuerpo: { actual, nueva },
                  });
                  setHecho(true);
                } catch (fallo) {
                  setError(
                    fallo instanceof Error
                      ? fallo.message
                      : t("contrasena.fallo"),
                  );
                } finally {
                  setEnviando(false);
                }
              }}
            >
              {enviando ? t("general.guardando") : t("contrasena.cambiar")}
            </button>
          </>
        )
      }
    >
      {hecho ? (
        <Aviso tipo="ok">{t("contrasena.cambiada")}</Aviso>
      ) : (
        <>
          <Aviso tipo="error">{error}</Aviso>
          <Campo etiqueta={t("contrasena.actual")}>
            <input
              type="password"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              autoFocus
            />
          </Campo>
          <Campo etiqueta={t("contrasena.nueva")} ayuda={t("entrada.minimo8")}>
            <input
              type="password"
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
            />
          </Campo>
          <Campo etiqueta={t("contrasena.repite")}>
            <input
              type="password"
              value={repetir}
              onChange={(e) => setRepetir(e.target.value)}
            />
          </Campo>
        </>
      )}
    </Modal>
  );
}
